import React from 'react';
import { Box, Paper, Typography } from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';

interface UploadSuccessProps {
  propertyName: string;
  documentsCount: number;
  contactEmail: string;
  managingCompany: string;
}

export const UploadSuccess: React.FC<UploadSuccessProps> = ({
  propertyName,
  documentsCount,
  contactEmail,
  managingCompany,
}) => {
  const getContactEmail = (company: string): string => {
    switch (company.toLowerCase()) {
      case 'barrett':
        return 'info@barretthtl.com';
      case 'horwath htl':
      default:
        return 'info@horwathhtl.com';
    }
  };

  const companyContactEmail = getContactEmail(managingCompany);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px',
        py: 6,
      }}
    >
      <Paper
        elevation={2}
        sx={{
          textAlign: 'center',
          p: 6,
          maxWidth: 550,
          borderRadius: 3,
        }}
      >
        <Box
          sx={{
            display: 'inline-flex',
            p: 2,
            borderRadius: '50%',
            backgroundColor: 'success.light',
            color: 'success.main',
            mb: 3,
          }}
        >
          <CheckCircleOutline sx={{ fontSize: 64, color: 'success.main' }} />
        </Box>

        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, color: 'success.dark' }}>
          Thank You
        </Typography>

        <Typography
          variant="body1"
          sx={{ mb: 3, lineHeight: 1.6 }}
        >
          We have received your documents for <strong>{propertyName}</strong>.
        </Typography>

        <Typography
          variant="h6"
          sx={{ mb: 3, fontWeight: 500 }}
        >
          Documents Received: {documentsCount}
        </Typography>

        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 4, lineHeight: 1.6 }}
        >
          You will receive a confirmation email at <strong>{contactEmail}</strong>
        </Typography>

        <Box
          sx={{
            backgroundColor: 'grey.50',
            borderRadius: 2,
            p: 3,
            mb: 4,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1 }}
          >
            If you have any questions, please contact us at:
          </Typography>
          <Typography
            variant="body1"
            component="a"
            href={`mailto:${companyContactEmail}`}
            sx={{
              color: 'primary.main',
              textDecoration: 'none',
              fontWeight: 500,
              '&:hover': {
                textDecoration: 'underline',
              },
            }}
          >
            {companyContactEmail}
          </Typography>
        </Box>

        <Typography
          variant="body1"
          sx={{ fontWeight: 500, color: 'text.secondary' }}
        >
          Thank you,
        </Typography>
        <Typography
          variant="h6"
          sx={{ fontWeight: 600, color: 'primary.main' }}
        >
          {managingCompany}
        </Typography>
      </Paper>
    </Box>
  );
};
